'use client';
import React, { useMemo, useEffect, useState } from 'react';
import {
  exploreProjects,
  project,
} from '@/components/utils/page-data/allProject';
import { IProject } from '@/components/type/type';
import ProjectCard from './ProjectCard';
import Container from '../ui/Container';
import SectionHeader from '../ui/SectionHeader';
import Link from 'next/link';
import AnimatedArrow from './AnimatedArrow';

type ExploreProjectProps = {
  numberOfCards: number;
  isInternal?: boolean;
  className?: string;
};

const ExploreProject = ({
  numberOfCards,
  isInternal,
  className,
}: ExploreProjectProps) => {
  const [isClient, setIsClient] = useState(false);
  const [hoverViewAll, setHoverViewAll] = useState<boolean>(false);
  const handleMouseEnterViewAll = () => setHoverViewAll(true);
  const handleMouseLeaveViewAll = () => {
    setHoverViewAll(false);
  };
  useEffect(() => {
    setIsClient(true);
  }, []);

  const shuffledProjects = useMemo(() => {
    if (!isClient) {
      // Return first N projects on server to avoid hydration mismatch
      return project.slice(0, numberOfCards);
    }

    // Shuffle on client only
    const projects = [...project];
    for (let i = projects.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [projects[i], projects[j]] = [projects[j], projects[i]];
    }
    return projects.slice(0, numberOfCards);
  }, [numberOfCards, isClient]);

  const exploreProjectsData = useMemo(() => {
    return shuffledProjects.map((proj: IProject) => ({
      projectImg: proj.image,
      projectName: proj.title,
      projectDescription: proj.description,
      redirectlink: proj.redirectlink,
    }));
  }, [shuffledProjects]);

  return (
    <div
      className={`bg-black bg-tech-we-work bg-full py-[60px] md:py-20 ${className || ''}`}
    >
      <Container className="flex flex-col gap-11">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
          <SectionHeader
            headingText={exploreProjects?.heading}
            descriptionText={exploreProjects?.description}
            headingStyle="!text-red "
            className="max-w-[800px] items-start"
            descriptionStyle="!text-start"
            isSectionDark={true}
          />
          <Link
            href={exploreProjects?.buttonLink}
            onMouseEnter={handleMouseEnterViewAll}
            onMouseLeave={handleMouseLeaveViewAll}
            onMouseUp={handleMouseEnterViewAll}
            onMouseDown={handleMouseLeaveViewAll}
            className="flex w-max items-center gap-1 rounded-[50px] font-opt text-base font-normal leading-tight text-white"
          >
            {exploreProjects?.buttonName}
            <AnimatedArrow hover={hoverViewAll} />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-x-10 md:gap-y-0 lg:gap-x-16 xl:grid-cols-3">
          {exploreProjectsData.map((item, i) => (
            <ProjectCard
              key={`${item.redirectlink}-${i}`}
              data={item}
              i={i}
              isInternal={isInternal}
            />
          ))}
        </div>
      </Container>
    </div>
  );
};

export default ExploreProject;
